import { Box, Paper, Typography, Switch, alpha, useTheme, Chip, Stack, IconButton, Tooltip, Drawer, Divider, Avatar, Button, Skeleton, Collapse } from "@mui/material";
import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LuUser, LuMail, LuPhone, LuShieldCheck, LuKey, LuCircleCheck, LuCircleX, LuBuilding, LuLayoutDashboard, LuFileText, LuShare2, LuPackage, LuZap, LuUsers, LuMapPin, LuCalendar, LuClock, LuCopy, LuShield, LuSave, LuX, LuMessageSquare } from "react-icons/lu";
import { IoAddOutline } from "react-icons/io5";
import { Grid, TextField, Checkbox } from "@mui/material";
import { useAuth } from "../../authentication/AuthProvider";
import { MdLogin } from "react-icons/md";
import AddButton from "../../components/button/AddButton";
import RefreshButton from "../../components/button/RefreshButton";
import ResizableBox from "../../components/container/ResizableBox";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ContextMenu from "../../components/context-menu/ContextMenu";
import DialogDelete from "../../components/dialog/DialogDelete";
import ConfirmationDialog from "../../components/dialog/ConfirmationDialog";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid from "../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, leftAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Company } from "../../db_access/company";
import { User } from "../../db_access/user";
import CheckBox from "../../components/input/CheckBox";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import MetricCard from "../../app_components/MetricCard";
import DonutChart from "../../components/chart/DonutChart";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import HorizontalStackedBarChart from "../../components/chart/HorizontalStackedBarChart";
import AdminGlobalView from "./AdminGlobalView";
dayjs.extend(relativeTime);

const BRAND_GREEN = '#659d33';

const StatusRenderer = ({ value, data, onStatusChange, tok, t }) => {
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onStatusChange(data.id, !value);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 1.2,
        cursor: "pointer",
        px: 1,
        height: "100%",
        transition: "opacity 0.2s ease",
        "&:hover": { opacity: 0.8 }
      }}
    >
      <Box sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: value ? "#10b981" : "#ef4444"
      }} />
      <Typography sx={{
        fontSize: "13px",
        fontWeight: 500,
        color: value ? "#059669" : "#dc2626",
        fontFamily: tok.font.family
      }}>
        {value ? t("Active") : t("Inactive")}
      </Typography>
    </Box>
  );
};

const PermissionChip = ({ label, icon: Icon, enabled, onToggle, tok, t }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      onClick={onToggle}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        p: '12px 16px',
        borderRadius: tok.radius.card,
        border: '1px solid',
        borderColor: enabled ? '#659d33' : tok.border.card,
        background: enabled ? alpha('#659d33', 0.08) : 'transparent',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        "&:hover": {
          background: enabled ? alpha('#659d33', 0.12) : alpha(tok.text.primary, 0.04),
          borderColor: enabled ? '#659d33' : tok.text.muted,
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: enabled ? '#659d33' : alpha(tok.text.primary, 0.05),
          transition: 'all 0.2s ease',
        }}>
          <Icon size={18} style={{ color: enabled ? '#fff' : tok.text.secondary }} />
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            color: enabled ? tok.text.primary : tok.text.secondary,
            fontFamily: tok.font.family
          }}
        >
          {t(label)}
        </Typography>
      </Box>

      {/* Simplified Flat Toggle Switch */}
      <Box
        sx={{
          position: 'relative',
          width: 44,
          height: 22,
          borderRadius: '11px',
          background: enabled ? '#659d33' : alpha(tok.text.primary, 0.15),
          transition: 'all 0.2s ease',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '3px',
            left: enabled ? 'calc(100% - 19px)' : '3px',
            width: 16,
            height: 16,
            borderRadius: '50%',
            bgcolor: '#fff',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {enabled && <LuCircleCheck size={10} color="#659d33" />}
        </Box>
      </Box>
    </Box>
  );
};

const ManageUsers = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { loginAsUser } = useAuth();
  const isDark = theme.palette.mode === 'dark';
  const tok = useMemo(() => getOrgTokens(isDark), [isDark]);
  const { refresh, setRefresh, enqueueAlert } = useContextProvider();

  const inputStyle = {
    '& .MuiInputBase-input': {
      height: 14,
      paddingY: 1.25,
      paddingX: 1.5,
      fontFamily: tok.font.family,
      fontSize: tok.font.size.gridCell,
      fontWeight: tok.font.weight.medium,
      color: isDark ? '#ffffff' : '#1e293b'
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      bgcolor: 'transparent'
    }
  };
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [searchText, setSearchText] = useState("");
  const [companies, setCompanies] = useState([]);

  // Filters
  const [orgFilter, setOrgFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  // Detail Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Impersonation
  const [impersonateTarget, setImpersonateTarget] = useState(null);

  // View Toggle: Registry vs Intelligence
  const [activeView, setActiveView] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const gridRef = useRef(null);
  const dialogRef = useRef(null);

  const getCompanies = () => {
    Company.get_all()
      .then((res) => {
        if (res.ok) {
          setIsAuthorized(true);
          return res.json();
        } else {
          setIsAuthorized(false);
          return [];
        }
      })
      .then((data) => setCompanies(data));
  };

  const getUsers = () => {
    User.get_all()
      .then((res) => res.json())
      .then((data) => {
        const mapped = data.map((d) => ({
          ...d,
          is_admin: d.is_admin === 1,
          is_active: d.is_active === 1,
          is_dpp_enabled: d.is_dpp_enabled === 1,
          is_collaborate_internal_enabled: d.is_collaborate_internal_enabled === 1,
          is_collaborate_external_enabled: d.is_collaborate_external_enabled === 1,
          is_product_enabled: d.is_product_enabled === 1,
          is_project_enabled: d.is_project_enabled === 1,
          is_building_enabled: d.is_building_enabled === 1,
          is_organization_enabled: d.is_organization_enabled === 1,
          is_ai_insight_enabled: d.is_ai_insight_enabled === 1,
          is_arivu_chat_enabled: d.is_arivu_chat_enabled === 1,
          is_company_admin: d.is_company_admin === 1,
          is_donor: d.is_donor === 1,
          is_receiver: d.is_receiver === 1,
          is_support_access_enabled: d.is_support_access_enabled === 1,
        }));
        setUsers(mapped);
        setFilteredUsers(mapped);
      });
  };

  const deleteUsers = (selectedUsers) => {
    User.delete(selectedUsers.map((s) => s.id)).finally(() => {
      setSelectedUsers([]);
      setRefresh((r) => !r);
    });
  };

  const newUser = () => {
    User.new().finally(() => setRefresh((r) => !r));
  };

  const handleRowClick = (params) => {
    setEditingUser({ ...params.data });
    setDrawerOpen(true);
  };

  const saveUserDetails = async () => {
    try {
      await User.update({
        ...editingUser,
        is_admin: editingUser.is_admin ? 1 : 0,
        is_active: editingUser.is_active ? 1 : 0,
        is_company_admin: editingUser.is_company_admin ? 1 : 0,
        is_building_enabled: editingUser.is_building_enabled ? 1 : 0,
        is_project_enabled: editingUser.is_project_enabled ? 1 : 0,
        is_dpp_enabled: editingUser.is_dpp_enabled ? 1 : 0,
        is_product_enabled: editingUser.is_product_enabled ? 1 : 0,
        is_organization_enabled: editingUser.is_organization_enabled ? 1 : 0,
        is_ai_insight_enabled: editingUser.is_ai_insight_enabled ? 1 : 0,
        is_arivu_chat_enabled: editingUser.is_arivu_chat_enabled ? 1 : 0,
        is_collaborate_internal_enabled: editingUser.is_collaborate_internal_enabled ? 1 : 0,
        is_collaborate_external_enabled: editingUser.is_collaborate_external_enabled ? 1 : 0,
        is_donor: editingUser.is_donor ? 1 : 0,
        is_receiver: editingUser.is_receiver ? 1 : 0,
      });
      setRefresh(r => !r);
      setEditingUser(null);
    } catch (error) {
      console.error("Failed to save user info", error);
    }
  };

  const handleImpersonateClick = (e, user) => {
    e.stopPropagation();
    setImpersonateTarget(user);
    // Open confirmation dialog
  };

  const confirmImpersonation = async () => {
    if (impersonateTarget) {
      try {
        await loginAsUser(impersonateTarget.id);
      } catch (error) {
        alert(t("Failed to login as user:") + " " + error.message);
      }
      setImpersonateTarget(null);
    }
  };

  const handleRequestSupportAccess = async (targetUser) => {
    try {
      await User.request_support_access(targetUser.id);
      enqueueAlert({ message: `${t("Access request sent to")} ${targetUser.user_id}`, severity: "success" });
      // Locally update the user object to show "Requested"
      setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, access_requested: true } : u));
      setFilteredUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, access_requested: true } : u));
    } catch (error) {
      console.error("Failed to request support access", error);
      enqueueAlert({ message: t("Failed to send request"), severity: "error" });
    }
  };

  const colDefs = [
    {
      headerName: t("Username"),
      field: "user_id",
      flex: 1.5,
      ...defaultColDef,
      ...leftAlignColDef,
      cellRenderer: (params) => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: '100%', px: 1 }}>
          <Typography variant="body2" sx={tok.gridCellText}>{params.value}</Typography>
        </Stack>
      )
    },
    {
      headerName: t("Role"),
      field: "is_admin",
      width: 180,
      ...defaultColDef,
      ...leftAlignColDef,
      cellRenderer: (params) => {
        const isAdmin = params.data.is_admin;
        const isCompanyAdmin = params.data.is_company_admin;
        let label = "Company User";
        let color = isDark ? "#ffffff" : "#1e293b";
        if (isAdmin) {
          label = "Master Admin"; color = BRAND_GREEN;
        } else if (isCompanyAdmin) {
          label = "Company Admin"; color = "#3b82f6";
        }

        return (
          <Typography sx={{ ...tok.gridCellText, color, fontWeight: 500 }}>{t(label)}</Typography>
        );
      }
    },
    {
      headerName: t("Organization"),
      field: "company_name",
      flex: 1.5,
      ...defaultColDef,
      ...leftAlignColDef,
      cellRenderer: p => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: '100%', px: 1 }}>
          <Typography variant="body2" sx={tok.gridCellText}>{p.value || "—"}</Typography>
        </Stack>
      )
    },
    {
      headerName: t("Status"),
      field: "is_active",
      width: 200,
      minWidth: 200,
      resizable: false,
      suppressSizeToFit: true,
      ...defaultColDef,
      ...centerAlignColDef,
      cellRenderer: (params) => <StatusRenderer {...params} tok={tok} t={t} onStatusChange={(id, status) => {
        User.update({ ...params.data, is_active: status ? 1 : 0 }).then(() => setRefresh(r => !r));
      }} />,
    },
    {
      headerName: t("Actions"),
      field: "id",
      width: 200,
      suppressMenu: true,
      sortable: false,
      resizable: false,
      ...centerAlignColDef,
      cellRenderer: (params) => {
        const isSupportEnabled = params.data.is_support_access_enabled;
        const isAdminUser = params.data.is_admin;

        if (isAdminUser) return null;

        if (isSupportEnabled) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
              <Tooltip title={t("Login as this user")}>
                <Button
                  size="small"
                  onClick={(e) => handleImpersonateClick(e, params.data)}
                  startIcon={<MdLogin />}
                  sx={{
                    width: 130,
                    height: 32,
                    borderRadius: 2,
                    textTransform: 'none',
                    border: '1px solid',
                    borderColor: alpha(theme.palette.warning.main, 0.4),
                    color: theme.palette.warning.main,
                    fontSize: '12px',
                    fontWeight: 700,
                    fontFamily: tok.font.family,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      borderColor: theme.palette.warning.main,
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  {t("Login As")}
                </Button>
              </Tooltip>
            </Box>
          );
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Tooltip title={t("Send a request to this user for support access")}>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRequestSupportAccess(params.data);
                }}
                disabled={params.data.access_requested}
                sx={{
                  width: 130,
                  height: 32,
                  borderRadius: 2,
                  textTransform: 'none',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  color: theme.palette.primary.main,
                  fontSize: '12px',
                  fontWeight: 700,
                  fontFamily: tok.font.family,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-disabled': {
                    borderColor: alpha(theme.palette.text.disabled, 0.2),
                    color: theme.palette.text.disabled
                  },
                  transition: 'all 0.2s'
                }}
              >
                {params.data.access_requested ? t("Requested") : t("Request Access")}
              </Button>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  const contextMenuOptions = [{ label: t("Delete"), action: () => dialogRef.current.open(), needSelectedItems: true }];

  useEffect(() => {
    getUsers();
    getCompanies();
  }, [refresh]);

  useEffect(() => {
    let filtered = users;
    if (orgFilter !== "All") filtered = filtered.filter(u => u.company_name === orgFilter);
    if (statusFilter !== "All") filtered = filtered.filter(u => (statusFilter === "Active" ? u.is_active : !u.is_active));
    if (typeFilter !== "All") {
      if (typeFilter === "Master Admin") filtered = filtered.filter(u => u.is_admin);
      else if (typeFilter === "Organization Admin") filtered = filtered.filter(u => u.is_company_admin);
      else filtered = filtered.filter(u => !u.is_admin && !u.is_company_admin);
    }

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(u =>
        u.user_id?.toLowerCase().includes(search) ||
        u.email?.toLowerCase().includes(search) ||
        u.company_name?.toLowerCase().includes(search)
      );
    }
    setFilteredUsers(filtered);
  }, [orgFilter, typeFilter, statusFilter, users, searchText]);

  const totalUsers = users.length;
  const activeCount = users.filter(c => c.is_active).length;
  const adminCount = users.filter(c => c.is_admin || c.is_company_admin).length;

  // Analytics Derivation
  const analytics = useMemo(() => {
    const statusData = [
      { label: t("Active Access"), value: activeCount, color: "#10b981" },
      { label: t("Revoked Access"), value: totalUsers - activeCount, color: "#ef4444" }
    ];

    // Users by Organization
    const orgMap = {};
    users.forEach(u => {
      const org = u.company_name || t("Unassigned");
      orgMap[org] = (orgMap[org] || 0) + 1;
    });
    const userByOrgData = Object.entries(orgMap)
      .map(([label, value]) => ({ label, Users: value }))
      .sort((a, b) => b.Users - a.Users)
      .slice(0, 10);

    return { statusData, userByOrgData };
  }, [users, activeCount, totalUsers, t]);

  return (
    isAuthorized && (
      <Stack spacing={tok.spacing.pageGap}>
        <Box sx={{
          ...tok.sectionHeading, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          pl: 0,
          position: 'relative',
          pb: tok.sectionHeading.titlePb,
          borderBottom: tok.sectionHeading.borderBottom
        }}>
          <Box sx={{ 
              width: tok.sectionHeading.barWidth, 
              height: '24px', 
              bgcolor: tok.sectionHeading.barColor, 
              borderRadius: '2px' 
          }} />
          <Typography sx={{ 
              fontFamily: tok.sectionHeading.fontFamily,
              fontSize: tok.sectionHeading.fontSize,
              fontWeight: tok.sectionHeading.fontWeight,
              color: tok.sectionHeading.color
          }}>
            {t("User Management")}
          </Typography>
        </Box>

        {/* Persistence Registry Summary */}
        <Box sx={{ display: 'flex', gap: tok.spacing.metricCardGap, flexWrap: 'wrap', width: '100%' }}>
          {isLoading ? (
            Array.from(new Array(3)).map((_, i) => (
              <Box key={i} sx={{ flex: '1 1 0', minWidth: '240px' }}>
                <Skeleton variant="rounded" height={100} sx={{ borderRadius: tok.radius.card }} />
              </Box>
            ))
          ) : (
            <>
              <Box sx={{ flex: '1 1 0', minWidth: '240px' }}>
                <MetricCard
                  title={t("Total Users")}
                  value={totalUsers}
                  icon={LuUsers}
                  accent={BRAND_GREEN}
                  bg={isDark ? alpha(BRAND_GREEN, 0.08) : alpha(BRAND_GREEN, 0.04)}
                />
              </Box>
              <Box sx={{ flex: '1 1 0', minWidth: '240px' }}>
                <MetricCard
                  title={t("Active Access")}
                  value={activeCount}
                  icon={LuShieldCheck}
                  accent="#10b981"
                  bg={isDark ? "rgba(16, 185, 129, 0.08)" : "rgba(16, 185, 129, 0.04)"}
                />
              </Box>
              <Box sx={{ flex: '1 1 0', minWidth: '240px' }}>
                <MetricCard
                  title={t("Elevated Privileges")}
                  value={adminCount}
                  icon={LuKey}
                  accent="#3b82f6"
                  bg={isDark ? "rgba(59, 130, 246, 0.08)" : "rgba(59, 130, 246, 0.04)"}
                />
              </Box>
            </>
          )}
        </Box>

        {/* Main Registry */}
        <Box sx={{
          borderRadius: tok.radius.card,
          bgcolor: tok.bg.card,
          border: '1px solid',
          borderColor: tok.border.card,
          boxShadow: tok.shadow.card,
          overflow: 'hidden'
        }}>
          <TableHeaderContainer>
            <TableTitleContainer>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <LuLayoutDashboard size={18} color={BRAND_GREEN} />
                <Typography sx={{ 
                  ...tok.tableTitle,
                  fontSize: '18px', // High density adjustment
                  fontWeight: 600
                }}>{t("User Accounts Ledger")}</Typography>
              </Box>
            </TableTitleContainer>
            <TableHeaderToolsContainer>
              <SearchBox text={searchText} setText={setSearchText} placeholder={t("Trace entries...")} width={300} />
              <AddButton onClick={newUser} />
              <ExportAsXLSXButton data={users} filename="user_ledger.xlsx" />
              <RefreshButton onClick={() => setRefresh(p => !p)} />
            </TableHeaderToolsContainer>
          </TableHeaderContainer>

          <Box sx={{ p: 0 }}>
            <AgGrid
              ref={gridRef}
              rowData={filteredUsers}
              columnDefs={colDefs}
              onRowClicked={handleRowClick}
              onSelectionChanged={(event) => setSelectedUsers(event.api.getSelectedRows())}
              handleContextMenu={(e) => { e.preventDefault(); setMenuPosition({ x: e.pageX, y: e.pageY }); setMenuVisible(true); }}
              checkboxes={false}
              headerCheckbox={false}
              pagination={true}
              rowHeight={52}
              domLayout="autoHeight"
              footerLeftContent={
                <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                  <CheckBox checked={activeView} onChange={(e) => setActiveView(e.target.checked)} label={t("Global Insights")} />
                </Box>
              }
              sx={{
                minHeight: 400,
                borderRadius: 0,
                overflow: 'hidden',
                border: 'none',
                bgcolor: 'transparent',
                boxShadow: 'none',
                "& .ag-root-wrapper": {
                  border: 'none',
                  bgcolor: 'transparent'
                },
                "& .ag-header": {
                  borderBottom: `1px solid ${tok.border.table}`,
                  bgcolor: tok.bg.tableHeader
                },
                "& .ag-header-cell": {
                  border: 'none !important',
                  borderRight: 'none !important',
                  fontFamily: tok.font.family,
                  fontSize: '12px',
                  color: tok.text.gridHeader
                },
                "& .ag-header-cell-label": {
                  justifyContent: "center"
                },
                "& .ag-header-cell::after, & .ag-header-separator, & .ag-header-cell-resizer": {
                  display: 'none !important',
                  width: '0 !important',
                  border: 'none !important',
                  background: 'transparent !important',
                  opacity: '0 !important'
                },
                "& .ag-pinned-right-header": {
                  borderLeft: 'none !important',
                  border: 'none !important',
                  background: 'transparent !important',
                  boxShadow: 'none !important'
                },
                "& .ag-header-viewport, & .ag-header-container": {
                  border: 'none !important',
                  background: 'transparent !important'
                },
                "& .ag-header-row": {
                  borderBottom: `1px solid ${tok.border.table} !important`,
                  borderTop: 'none !important',
                  background: 'transparent !important'
                },
                "& .ag-cell": {
                  border: 'none !important',
                  borderRight: 'none !important',
                  fontFamily: tok.font.family
                },
                "& .ag-pinned-right-cols-container": {
                  borderLeft: 'none !important',
                  border: 'none !important'
                },
                "& .ag-row": {
                  borderBottom: `1px solid ${tok.border.row}`,
                  fontFamily: tok.font.family
                },
                "& .ag-row-even": {
                  bgcolor: isDark ? alpha('#fff', 0.01) : alpha('#f8f9fa', 0.5)
                },
                "& .ag-row-odd": {
                  bgcolor: 'transparent'
                },
                "& .ag-row:hover": {
                  bgcolor: tok.bg.rowHover
                },
                "& .ag-cell": {
                  borderRight: 'none',
                  px: '24px !important',
                  fontSize: tok.font.size.gridCell,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            />
          </Box>
        </Box>

        <Collapse in={!!editingUser}>
          <Paper sx={{
            width: '100%',
            p: 2.5,
            pt: 1.5,
            borderRadius: tok.radius.card,
            bgcolor: tok.bg.card,
            border: '1px solid',
            borderColor: tok.border.card,
            boxShadow: tok.shadow.cardHover,
            position: 'relative'
          }}>
            {/* Form Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar variant="rounded" sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: alpha(BRAND_GREEN, 0.08), color: BRAND_GREEN }}>
                  <LuUser size={20} />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: tok.font.weight.medium, color: isDark ? "#ffffff" : "#1e293b", fontFamily: tok.font.family, fontSize: tok.font.size.sectionTitle, letterSpacing: '-0.5px' }}>
                    {editingUser?.user_id || t("Entity Modification Interface")}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={() => setEditingUser(null)} size="small" sx={{ bgcolor: alpha(tok.text.primary, 0.04), border: '1px solid', borderColor: alpha(tok.text.primary, 0.05), '&:hover': { bgcolor: alpha(tok.text.primary, 0.08) } }}>
                <LuX size={18} />
              </IconButton>
            </Box>

            {editingUser && (
              <Box>
                {/* Row 1: 5 Columns */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 3, mb: 3 }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: tok.font.weight.medium, color: isDark ? "#ffffff" : "#1e293b", mb: 0.8, display: 'block', textTransform: 'none', fontFamily: tok.font.family, fontSize: tok.font.size.gridCell }}>{t("Username")}</Typography>
                    <TextField fullWidth value={editingUser.user_id || ""} onChange={(e) => setEditingUser({ ...editingUser, user_id: e.target.value })} sx={inputStyle} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: tok.font.weight.medium, color: isDark ? "#ffffff" : "#1e293b", mb: 0.8, display: 'block', textTransform: 'none', fontFamily: tok.font.family, fontSize: tok.font.size.gridCell }}>{t("Password")}</Typography>
                    <TextField fullWidth type="password" value={editingUser.password || ""} onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })} sx={inputStyle} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: tok.font.weight.medium, color: isDark ? "#ffffff" : "#1e293b", mb: 0.8, display: 'block', textTransform: 'none', fontFamily: tok.font.family, fontSize: tok.font.size.gridCell }}>{t("Email Address")}</Typography>
                    <TextField fullWidth value={editingUser.email || ""} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} sx={inputStyle} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: tok.font.weight.medium, color: isDark ? "#ffffff" : "#1e293b", mb: 0.8, display: 'block', textTransform: 'none', fontFamily: tok.font.family, fontSize: tok.font.size.gridCell }}>{t("Phone Contact")}</Typography>
                    <TextField fullWidth value={editingUser.phone_number || ""} onChange={(e) => setEditingUser({ ...editingUser, phone_number: e.target.value })} sx={inputStyle} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: tok.font.weight.medium, color: isDark ? "#ffffff" : "#1e293b", mb: 0.8, display: 'block', textTransform: 'none', fontFamily: tok.font.family, fontSize: tok.font.size.gridCell }}>{t("Organization")}</Typography>
                    <select value={editingUser.company_id || ""} onChange={(e) => setEditingUser({ ...editingUser, company_id: e.target.value })} style={{ width: '100%', height: '40px', padding: '0 12px', borderRadius: '4px', border: `1px solid ${tok.border.card}`, outline: 'none', fontWeight: tok.font.weight.medium, fontSize: tok.font.size.gridCell, backgroundColor: 'transparent', color: isDark ? "#ffffff" : "#1e293b", fontFamily: tok.font.family }}>
                      <option value="" style={{ color: '#000' }}>{t("Select Organization")}</option>
                      {companies.map(c => <option key={c.id} value={c.id} style={{ color: '#000' }}>{c.name}</option>)}
                    </select>
                  </Box>
                </Box>

                {/* Row 2: Roles and Status */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 3fr) 2fr', gap: 3, mb: 4 }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: tok.font.weight.medium, color: isDark ? "#ffffff" : "#1e293b", mb: 0.8, display: 'block', textTransform: 'none', fontFamily: tok.font.family, fontSize: tok.font.size.gridCell }}>{t("Primary Role")}</Typography>
                    <Stack direction="row" spacing={0} sx={{ height: 40, borderRadius: '4px', overflow: 'hidden', border: '1px solid', borderColor: tok.border.card }}>
                      <Button
                        fullWidth
                        onClick={() => setEditingUser({ ...editingUser, is_company_admin: false, is_admin: false })}
                        sx={{
                          borderRadius: 0, py: 0,
                          fontFamily: tok.font.family, fontWeight: tok.font.weight.medium, fontSize: tok.font.size.gridCell, textTransform: 'none',
                          bgcolor: (!editingUser.is_company_admin && !editingUser.is_admin) ? alpha(BRAND_GREEN, 0.1) : 'transparent',
                          color: (!editingUser.is_company_admin && !editingUser.is_admin) ? BRAND_GREEN : (isDark ? "#ffffff" : "#1e293b"),
                          borderRight: '1px solid', borderColor: tok.border.card
                        }}
                      >{t("Company User")}</Button>
                      <Button
                        fullWidth
                        onClick={() => setEditingUser({ ...editingUser, is_company_admin: true, is_admin: false })}
                        sx={{
                          borderRadius: 0, py: 0,
                          fontFamily: tok.font.family, fontWeight: tok.font.weight.medium, fontSize: tok.font.size.gridCell, textTransform: 'none',
                          bgcolor: editingUser.is_company_admin ? alpha(BRAND_GREEN, 0.1) : 'transparent',
                          color: editingUser.is_company_admin ? BRAND_GREEN : (isDark ? "#ffffff" : "#1e293b")
                        }}
                      >{t("Company Admin")}</Button>
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: tok.font.weight.medium, color: isDark ? "#ffffff" : "#1e293b", mb: 0.8, display: 'block', textTransform: 'none', fontFamily: tok.font.family, fontSize: tok.font.size.gridCell }}>{t("Account Visibility")}</Typography>
                    <Box sx={{ p: 1.25, px: 1.5, height: 40, borderRadius: '4px', bgcolor: 'transparent', border: '1px solid', borderColor: tok.border.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: tok.font.weight.medium, fontSize: tok.font.size.gridCell, color: isDark ? "#ffffff" : "#1e293b", fontFamily: tok.font.family }}>{editingUser.is_active ? t("Active") : t("Restricted")}</Typography>
                      <Switch checked={editingUser.is_active} onChange={(e) => setEditingUser({ ...editingUser, is_active: e.target.checked })} size="small" />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: tok.font.weight.medium, color: isDark ? "#ffffff" : "#1e293b", mb: 1.5, display: 'block', textTransform: 'none', fontFamily: tok.font.family, fontSize: tok.font.size.gridCell }}>{t("Module Permissions")}</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
                    <PermissionChip label="Buildings" icon={LuBuilding} enabled={editingUser.is_building_enabled} onToggle={() => setEditingUser({ ...editingUser, is_building_enabled: !editingUser.is_building_enabled })} tok={tok} t={t} />
                    <PermissionChip label="Projects" icon={LuCalendar} enabled={editingUser.is_project_enabled} onToggle={() => setEditingUser({ ...editingUser, is_project_enabled: !editingUser.is_project_enabled })} tok={tok} t={t} />
                    <PermissionChip label="DPP Access" icon={LuFileText} enabled={editingUser.is_dpp_enabled} onToggle={() => setEditingUser({ ...editingUser, is_dpp_enabled: !editingUser.is_dpp_enabled })} tok={tok} t={t} />
                    <PermissionChip label="AI Insight" icon={LuZap} enabled={editingUser.is_ai_insight_enabled} onToggle={() => setEditingUser({ ...editingUser, is_ai_insight_enabled: !editingUser.is_ai_insight_enabled })} tok={tok} t={t} />
                    <PermissionChip label="Arivu Chat" icon={LuMessageSquare} enabled={editingUser.is_arivu_chat_enabled} onToggle={() => setEditingUser({ ...editingUser, is_arivu_chat_enabled: !editingUser.is_arivu_chat_enabled })} tok={tok} t={t} />
                    <PermissionChip label="Products" icon={LuPackage} enabled={editingUser.is_product_enabled} onToggle={() => setEditingUser({ ...editingUser, is_product_enabled: !editingUser.is_product_enabled })} tok={tok} t={t} />
                    <PermissionChip label="Organizations" icon={LuBuilding} enabled={editingUser.is_organization_enabled} onToggle={() => setEditingUser({ ...editingUser, is_organization_enabled: !editingUser.is_organization_enabled })} tok={tok} t={t} />
                    <PermissionChip label="Internal Collab" icon={LuShare2} enabled={editingUser.is_collaborate_internal_enabled} onToggle={() => setEditingUser({ ...editingUser, is_collaborate_internal_enabled: !editingUser.is_collaborate_internal_enabled })} tok={tok} t={t} />
                    <PermissionChip label="External Collab" icon={LuShare2} enabled={editingUser.is_collaborate_external_enabled} onToggle={() => setEditingUser({ ...editingUser, is_collaborate_external_enabled: !editingUser.is_collaborate_external_enabled })} tok={tok} t={t} />
                  </Box>
                </Box>

                {/* Action Toolbar */}
                <Box sx={{ mt: 5, pt: 3, borderTop: `1px solid ${tok.border.card}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button variant="outlined" onClick={() => setEditingUser(null)} sx={{ px: 4, py: 1, borderRadius: '4px', borderColor: tok.border.card, color: isDark ? "#ffffff" : "#1e293b", fontFamily: tok.font.family, fontWeight: tok.font.weight.medium, fontSize: tok.font.size.gridCell, textTransform: 'none', '&:hover': { bgcolor: alpha(tok.text.primary, 0.05), borderColor: tok.text.primary } }}>
                    {t("Discard")}
                  </Button>
                  <Button variant="contained" startIcon={<LuSave size={18} />} onClick={saveUserDetails} sx={{ px: 5, py: 1, borderRadius: '4px', bgcolor: "#659d33", fontFamily: tok.font.family, fontWeight: tok.font.weight.medium, fontSize: tok.font.size.gridCell, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: "#4d7a26", boxShadow: 'none' } }}>
                    {t("Commit Changes")}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Collapse>

        {/* Intelligence perspective (Global Analytics) */}
        <Collapse in={activeView && !editingUser}>
          <Box>
            <AdminGlobalView
              overrideDonutData={analytics.statusData.map(d => ({
                ...d,
                percent: (d.value / users.length) * 100
              }))}
              overrideBarData={analytics.userByOrgData}
              barKey="Users"
              donutTitle={t("Access Topology")}
              barTitle={t("User Demographic by Organization")}
              unitLabel={t("Users")}
              accentColor={BRAND_GREEN}
              hideFilters={true}
              hideLeaders={true}
            />
          </Box>
        </Collapse>
        <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={() => setMenuVisible(false)} selectedItems={selectedUsers} options={contextMenuOptions} />
        <DialogDelete selectedItems={selectedUsers} handleDelete={deleteUsers} ref={dialogRef} label={"user_id"} />

        {impersonateTarget && (
          <ConfirmationDialog
            open={!!impersonateTarget}
            title={`${t("Login as")} ${impersonateTarget.user_id}?`}
            content={t('You differ from this user in permissions and data access. You will be logged out of your current session and logged in as this user for troubleshooting purposes.\n\nTo return to your admin account, click the "Exit Support View" button in the orange banner at the top of the screen.')}
            onConfirm={confirmImpersonation}
            onCancel={() => setImpersonateTarget(null)}
            confirmText={t("Login As User")}
            confirmColor="warning"
          />
        )}
      </Stack>
    )
  );
};
export default ManageUsers;
